import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import type { StockItemView, StoreView } from '@wodi/shared';
import { Button, Notice, PageHeader, RadioGroup, TextArea, TextField } from '@wodi/ui';
import { useState } from 'react';

import { createTransfer, fefoPreview } from '../api/stock.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatIsoDate } from '../patients/format.ts';
import { BackToStore, errorText, newRequestKey, WithStore } from '../stock/common.tsx';
import { formatQuantity, readQuantity } from '../stock/format.ts';
import { StockItemPicker } from '../stock/item-picker.tsx';
import { screen } from '../layout/screen.tsx';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

const route = getRouteApi('/_app/stores/$storeId/transfer');

interface Line {
  key: string;
  item: StockItemView;
  quantityText: string;
}

/**
 * Transfer stock (plan §5.4, decision 10): from this store to another at the facility, in
 * one step. The batches that expire first go first; the preview shows which.
 */
export function TransferStockPage() {
  const { storeId } = route.useParams();
  return (
    <WithStore storeId={storeId}>
      {(store, stores) => <TransferForm from={store} stores={stores} />}
    </WithStore>
  );
}

function TransferForm({ from, stores }: { from: StoreView; stores: StoreView[] }) {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const others = stores.filter((s) => s.id !== from.id);
  const [toStoreId, setToStoreId] = useState<string | null>(
    others.length === 1 ? (others[0]?.id ?? null) : null,
  );
  const [lines, setLines] = useState<Line[]>([]);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [requestKey] = useState(newRequestKey);

  const valid =
    toStoreId !== null &&
    lines.length > 0 &&
    lines.every((l) => (readQuantity(l.quantityText) ?? 0) > 0);

  const transfer = useMutation({
    mutationFn: () =>
      createTransfer({
        clientRequestId: requestKey,
        fromStoreId: from.id,
        toStoreId: toStoreId ?? '',
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        lines: lines.map((l) => ({
          itemId: l.item.itemId,
          quantity: readQuantity(l.quantityText) ?? 0,
        })),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['stock'] });
      toasts.show({ title: 'Stock transferred', description: result.number });
      void navigate({ to: '/stores', search: { storeId: from.id } });
    },
  });

  if (others.length === 0)
    return (
      <>
        <BackToStore storeId={from.id} name={from.name} />
        <PageHeader title="Transfer stock" />
        <Notice tone="info">
          This facility has only one active store. Add another in Settings to transfer stock.
        </Notice>
      </>
    );

  return (
    <div className={styles.page}>
      <BackToStore storeId={from.id} name={from.name} />
      <PageHeader title={`Transfer stock from ${from.name}`} />
      <form
        className={`${styles.wideForm} ${screen.panel}`}
        onSubmit={(e) => {
          e.preventDefault();
          setTried(true);
          if (valid) transfer.mutate();
        }}
      >
        <RadioGroup
          label="To"
          options={others.map((s) => ({ id: s.id, label: s.name }))}
          {...(toStoreId === null ? {} : { value: toStoreId })}
          onChange={setToStoreId}
          {...(tried && toStoreId === null
            ? { errorMessage: 'Choose where the stock is going.' }
            : {})}
        />
        <StockItemPicker
          label="Add an item"
          onSelect={(item) => {
            if (lines.some((l) => l.item.itemId === item.itemId)) return;
            setLines((all) => [...all, { key: newRequestKey(), item, quantityText: '' }]);
          }}
        />
        {lines.map((line) => (
          <TransferLine
            key={line.key}
            storeId={from.id}
            line={line}
            tried={tried}
            onChange={(quantityText) => {
              setLines((all) => all.map((l) => (l.key === line.key ? { ...l, quantityText } : l)));
            }}
            onRemove={() => {
              setLines((all) => all.filter((l) => l.key !== line.key));
            }}
          />
        ))}
        <TextArea label="Note (optional)" value={note} onChange={setNote} />
        {transfer.error === null ? null : (
          <Notice tone="error">
            {errorText(transfer.error, "The stock wasn't transferred. Try again.")}
          </Notice>
        )}
        {online ? null : (
          <Notice tone="info">
            Transferring stock needs a connection to Wodi. Reconnect, then try again.
          </Notice>
        )}
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={transfer.isPending}
            pendingLabel="Transferring…"
          >
            Transfer stock
          </Button>
        </div>
      </form>
    </div>
  );
}

function TransferLine({
  storeId,
  line,
  tried,
  onChange,
  onRemove,
}: {
  storeId: string;
  line: Line;
  tried: boolean;
  onChange: (text: string) => void;
  onRemove: () => void;
}) {
  const quantity = readQuantity(line.quantityText);
  const unit = line.item.stock?.baseUnit.display ?? '';
  const preview = useQuery({
    queryKey: ['fefo-preview', storeId, line.item.itemId, quantity],
    queryFn: () => fefoPreview(storeId, line.item.itemId, quantity ?? 1),
    enabled: quantity !== null && quantity > 0,
    retry: false,
  });
  return (
    <fieldset className={styles.line}>
      <legend className={styles.lineHeading}>{line.item.name}</legend>
      <TextField
        label="Quantity"
        unit={unit}
        inputMode="numeric"
        value={line.quantityText}
        onChange={onChange}
        {...(tried && (quantity ?? 0) === 0 ? { errorMessage: 'Enter how many to move.' } : {})}
      />
      {preview.isSuccess ? (
        <p className={styles.meta}>
          Batches that go first:{' '}
          {preview.data.allocations
            .map(
              (a) =>
                `${a.batchNumber}${a.expiryDate === null ? '' : ` (expires ${formatIsoDate(a.expiryDate)})`}: ${formatQuantity(a.quantity, unit)}`,
            )
            .join('; ')}
        </p>
      ) : null}
      {preview.isError ? (
        <Notice tone="error">
          {errorText(preview.error, "Couldn't check the stock for this line.")}
        </Notice>
      ) : null}
      <div className={styles.actions}>
        <Button variant="quiet" onPress={onRemove}>
          Remove line
        </Button>
      </div>
    </fieldset>
  );
}
